import ApiService from "../../services/ApiService";
import Logger from "../../services/Logger";
import { TUTOR_DETAILS_KEYS, readTutorLibraryAll } from "./tutorHubDetails";
import { applyMeaningsToPuzzle } from "./tutorCrosswordFromText";

const PLACEHOLDERS = new Set([
    "used in this puzzle's story.",
    "a word to find in this puzzle.",
]);

export function isRealTutorClue(value) {
    const text = String(value || "").trim();
    return Boolean(text) && !PLACEHOLDERS.has(text.toLowerCase());
}

function addClue(map, word, definition) {
    const cleanWord = String(word || "").trim();
    const cleanDefinition = String(definition || "").trim();
    if (!cleanWord || !isRealTutorClue(cleanDefinition)) {
        return;
    }
    const key = cleanWord.toLowerCase();
    if (!map.has(key)) {
        map.set(key, { word: cleanWord, definition: cleanDefinition });
    }
}

function meaningMap(clues) {
    return new Map([...clues.values()].map((row) => [row.word.toLowerCase(), row.definition]));
}

/**
 * Use clues the story already has. Ask Bedrock only for words that still have none.
 * @returns {{ puzzle: object, clues: {word: string, definition: string}[], changed: boolean }}
 */
export async function fillTutorPuzzleClues({ puzzle, savedClues, title, sourceText, storyId }) {
    const known = new Map();
    for (const row of savedClues || []) {
        addClue(known, row?.word, row?.definition || row?.meaning);
    }
    for (const placement of puzzle?.placements || []) {
        if (placement?.fromTutorDef) {
            addClue(known, placement.word, placement.meaning);
        }
    }

    const withKnown = applyMeaningsToPuzzle(puzzle, meaningMap(known));
    const missing = [];
    const seen = new Set();
    for (const placement of withKnown?.placements || []) {
        const key = String(placement.word || "").trim().toLowerCase();
        if (!key || seen.has(key) || isRealTutorClue(placement.meaning)) {
            continue;
        }
        seen.add(key);
        missing.push(placement.word);
    }
    if (missing.length === 0) {
        return { puzzle: withKnown, clues: [...known.values()], changed: false };
    }

    try {
        const response = await ApiService.postData("/v1/tutor/clues", {
            title: title || "",
            sourceText: sourceText || "",
            storyId: storyId || "",
            words: missing,
            known: [...known.values()],
        });
        const returned = Array.isArray(response?.clues) ? response.clues : [];
        for (const row of returned) {
            addClue(known, row?.word, row?.definition);
        }
    } catch (err) {
        Logger.error("Tutor clue request failed:", err);
    }

    const savedKeys = new Set(
        (savedClues || [])
            .map((row) => String(row?.word || "").trim().toLowerCase())
            .filter(Boolean),
    );
    const clues = [...known.values()];
    const changed = clues.some((row) => !savedKeys.has(row.word.toLowerCase()));
    return {
        puzzle: applyMeaningsToPuzzle(puzzle, meaningMap(known)),
        clues,
        changed,
    };
}

export async function saveTutorStoryClues(details, storyId, clues, patchDetails) {
    const nextLibrary = readTutorLibraryAll(details).map((item) => (
        item.id === storyId ? { ...item, clues } : item
    ));
    await patchDetails({
        [TUTOR_DETAILS_KEYS.library]: nextLibrary,
    });
}
